import React, { useMemo } from 'react'
import styled from '@emotion/styled'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { CollateralBasketDetail, EntityType, Collateral, ProductSector, Product, Market } from 'api'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { productSectorRecordSelector, productRecordSelector } from 'selectors/productSelectors'
import { Translation, Locale } from 'locale'
import { CollateralBasketProductsPanelGridRow } from './CollateralBasketDialogUtils'
import { getCollateralStatusColor } from 'view/shared/datagrid'

interface ReadOnlyCollateralBasketProductsPanelProps {
  collateralBasketDetail: CollateralBasketDetail
  getCollateral: (collateralId: string) => Collateral | null
  productSectors: Record<string, ProductSector>
  products: Record<string, Product>
}

interface ProductListItemProps {
  data: CollateralBasketProductsPanelGridRow
}

export const mapStateToProps = createStructuredSelector({
  getCollateral: createEntityGetterSelector(EntityType.COLLATERAL),
  productSectors: productSectorRecordSelector,
  products: productRecordSelector
})

const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 20px 10px 0;
  height: 450px;
`

const ListTitle = styled.div`
  font-weight: bold;
  display: flex;
  flex-direction: row;
  padding: 0 0 5px 10px;

  div:first-child,
  div:nth-child(2) {
    width: 70px;
  }

  div:nth-child(3),
  div:nth-child(4) {
    width: 160px;
  }
`

const ListItems = styled.div`
  height: 450px;
  overflow-y: auto;
  padding-right: 5px;
`

const ListItemContainer = styled.div<{ statusColor: string }>`
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid #e1e3e7;
  border-right: 1px solid #e1e3e7;
  border-left: 6px solid ${props => props.statusColor};
  padding: 0 4px;

  :first-child {
    border-top: 1px solid #e1e3e7;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
  }

  :last-child {
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }
`

const ListItem = styled.div`
  display: flex;
  align-items: center;
  height: 35px;

  :first-child,
  :nth-child(2) {
    width: 70px;
  }

  :nth-child(3),
  :nth-child(4) {
    width: 160px;
  }

  :last-child {
    border-bottom-left-radius: 6px;

    :first-child {
      border-bottom-left-radius: 6px;
    }
  }
`

const ProductListItem: React.FunctionComponent<ProductListItemProps> = props => {
  const { product, productSector, collateral } = props.data
  return (
    <ListItemContainer statusColor={getCollateralStatusColor(collateral!.status)}>
      <ListItem>{product?.name}</ListItem>
      <ListItem>{productSector?.name}</ListItem>
      <ListItem>{collateral?.name}</ListItem>
      <ListItem>{collateral?.id}</ListItem>
    </ListItemContainer>
  )
}

const ReadOnlyCollateralBasketProductsPanelComponent: React.FunctionComponent<ReadOnlyCollateralBasketProductsPanelProps> = props => {
  const { collateralBasketDetail, getCollateral, productSectors, products } = props

  const rowData = useMemo(() => {
    return collateralBasketDetail.collateralBasket.collateralIds.map(collateralId => {
      const collateral = getCollateral(collateralId)
      return {
        rowId: collateralId,
        collateral,
        collaterals: [],
        productSector: collateral ? productSectors[collateral.productSectorId] : null,
        product: collateral ? products[productSectors[collateral.productSectorId].productId] : null,
        selected: false
      }
    })
  }, [collateralBasketDetail, productSectors, products, getCollateral])

  return (
    <ProductList>
      <ListTitle>
        <div>{Locale.translate(Translation.TC_PRODUCT)}</div>
        <div>{Locale.translate(Translation.TC_SECTOR)}</div>
        <div>{Locale.translate(Translation.TC_COLLATERAL)}</div>
        <div>{Locale.translate(collateralBasketDetail.product?.market === Market.US ? Translation.TC_CUSIP : Translation.TC_ISIN)}</div>
      </ListTitle>
      <ListItems>
        {rowData.map((row: CollateralBasketProductsPanelGridRow) => (
          <ProductListItem key={row.rowId} data={row} />
        ))}
      </ListItems>
    </ProductList>
  )
}

export const ReadOnlyCollateralBasketProductsPanel = connect(mapStateToProps)(ReadOnlyCollateralBasketProductsPanelComponent)
